import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Sparkles,
  Target,
  Wind,
  Plus,
  Send,
  Bookmark,
  Trash2,
  BookMarked,
  X,
  Menu,
  Volume2,
  VolumeX,
  Orbit,
} from "lucide-react";
import { toast } from "sonner";
import { Starfield } from "../components/Starfield";
import { DiamondMark } from "../components/DiamondMark";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { api, formatApiErrorDetail, streamMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { startAmbient, stopAmbient } from "../lib/ambient";
import { ManifestTracker, parseStep } from "../components/ManifestTracker";
import { AiContent, splitAiContent } from "../components/AiContent";

const MODES = [
  { id: "reflect", label: "Reflect", icon: Eye, tag: "Self-Inquiry" },
  { id: "belief_clearing", label: "Clear a Belief", icon: Sparkles, tag: "Pattern Release" },
  { id: "intention", label: "Set an Intention", icon: Target, tag: "Manifestation" },
  { id: "manifest", label: "Manifest", icon: Orbit, tag: "Desire → Rewrite" },
  { id: "quiet_minute", label: "Quiet Minute", icon: Wind, tag: "Stillness & Breath" },
];
const MODE_MAP = Object.fromEntries(MODES.map((m) => [m.id, m]));

function BreathOverlay({ onClose }) {
  const [seconds, setSeconds] = useState(60);
  const [phase, setPhase] = useState("Breathe in");
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => (s <= 1 ? (clearInterval(t), 0) : s - 1)), 1000);
    const p = setInterval(() => setPhase((ph) => (ph === "Breathe in" ? "Breathe out" : "Breathe in")), 4000);
    return () => { clearInterval(t); clearInterval(p); };
  }, []);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-cg-deep/95 backdrop-blur-xl flex flex-col items-center justify-center"
      data-testid="breath-overlay"
    >
      <button onClick={onClose} className="absolute top-6 right-6 text-cg-texts hover:text-cg-textp" data-testid="breath-close">
        <X className="h-6 w-6" />
      </button>
      <div className="relative h-64 w-64 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-cg-violet/30 blur-2xl animate-breathe" />
        <div className="absolute inset-6 rounded-full border border-cg-cyan/40 animate-breathe" style={{ animationDelay: "0.3s" }} />
        <span className="relative font-serif text-2xl text-cg-textp">{phase}</span>
      </div>
      <p className="mt-10 font-mono text-sm uppercase tracking-[0.3em] text-cg-cyan">{seconds}s</p>
      <p className="mt-3 text-sm text-cg-texts">Let the next thing wait. Just this breath.</p>
    </motion.div>
  );
}

export default function Workspace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showModePicker, setShowModePicker] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journal, setJournal] = useState([]);
  const [breath, setBreath] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const logRef = useRef(null);

  const toggleSound = useCallback((on) => {
    setSoundOn((prev) => {
      const next = typeof on === "boolean" ? on : !prev;
      if (next) startAmbient();
      else stopAmbient();
      return next;
    });
  }, []);

  useEffect(() => () => stopAmbient(), []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/sessions");
      setSessions(data);
    } catch {}
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const openSession = async (s) => {
    setActive(s);
    setSidebarOpen(false);
    if (s.mode === "quiet_minute" || s.mode === "belief_clearing") toggleSound(true);
    try {
      const { data } = await api.get(`/sessions/${s.session_id}`);
      setMessages(data.messages);
      if (s.mode === "quiet_minute" && data.messages.length === 0) setBreath(true);
    } catch {
      setMessages([]);
    }
  };

  const startSession = async (mode) => {
    setShowModePicker(false);
    try {
      const { data } = await api.post("/sessions", { mode });
      await loadSessions();
      setActive(data);
      setMessages([]);
      if (mode === "quiet_minute" || mode === "belief_clearing") toggleSound(true);
      if (mode === "quiet_minute") setBreath(true);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(msg);
      if (err.response?.status === 402) navigate("/pricing");
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !active || sending) return;
    setInput("");
    setSending(true);
    const streamId = "streaming";
    let userMsgId = "tmp";
    setMessages((m) => [...m, { message_id: "tmp", role: "user", content: text }]);

    const fail = (detail, status) => {
      setMessages((m) => m.filter((x) =>
        x.message_id !== "tmp" && x.message_id !== streamId && x.message_id !== userMsgId));
      setSending(false);
      toast.error(detail || "Message failed");
      if (status === 402) navigate("/pricing");
    };
    const withTimeout = (p, ms) => Promise.race([p, new Promise((_, rej) =>
      setTimeout(() => rej(new Error("AI took too long to respond. Please try again.")), ms))]);

    // Admin/demo accounts run inference client-side via Puter (free credits), so
    // reflections keep working for demos even if the app's AI credits are depleted.
    if (user?.use_puter && window.puter?.ai?.chat) {
      try {
        const { data } = await api.post(`/sessions/${active.session_id}/puter-prepare`, { content: text });
        userMsgId = data.user_message.message_id;
        setMessages((m) => [...m.filter((x) => x.message_id !== "tmp"), data.user_message,
          { message_id: streamId, role: "assistant", content: "" }]);
        const stream = await withTimeout(window.puter.ai.chat(data.messages, { model: "claude-sonnet-5-5", stream: true }), 45000);
        const it = stream[Symbol.asyncIterator]();
        let full = "";
        let first = true;
        while (true) {
          const { value: part, done: end } = first ? await withTimeout(it.next(), 45000) : await it.next();
          first = false;
          if (end) break;
          const chunk = (part && (part.text || part?.message?.content)) || "";
          if (chunk) {
            full += chunk;
            setMessages((m) => m.map((x) =>
              x.message_id === streamId ? { ...x, content: x.content + chunk } : x));
          }
        }
        if (!full.trim()) throw new Error("No response from Puter AI");
        const { data: done } = await api.post(`/sessions/${active.session_id}/puter-complete`, { content: full });
        setMessages((m) => m.map((x) => (x.message_id === streamId ? done.assistant_message : x)));
        setSending(false);
        loadSessions();
      } catch (err) {
        if (userMsgId !== "tmp") api.delete(`/sessions/${active.session_id}/messages/${userMsgId}`).catch(() => {});
        fail("Puter AI: " + (err?.message || err?.error?.message || "could not generate a reply"));
      }
      return;
    }

    await streamMessage(active.session_id, text, {
      onUser: (msg) => {
        userMsgId = msg.message_id;
        setMessages((m) => [...m.filter((x) => x.message_id !== "tmp"), msg,
          { message_id: streamId, role: "assistant", content: "" }]);
      },
      onDelta: (chunk) => {
        setMessages((m) => m.map((x) =>
          x.message_id === streamId ? { ...x, content: x.content + chunk } : x));
      },
      onDone: (msg) => {
        setMessages((m) => m.map((x) => (x.message_id === streamId ? msg : x)));
        setSending(false);
        loadSessions();
      },
      onError: (err) => fail(err.detail, err.status),
    });
  };

  const saveToJournal = async (content) => {
    try {
      await api.post("/journal", { session_id: active?.session_id, mode: active?.mode || "reflect", content });
      toast.success("Saved to your journal");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      toast.error(msg);
      if (err.response?.status === 402) navigate("/pricing");
    }
  };

  const openJournal = async () => {
    try {
      const { data } = await api.get("/journal");
      setJournal(data);
      setJournalOpen(true);
    } catch {}
  };

  const deleteJournal = async (id) => {
    await api.delete(`/journal/${id}`);
    setJournal((j) => j.filter((e) => e.entry_id !== id));
  };

  const deleteSession = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/sessions/${id}`);
    if (active?.session_id === id) { setActive(null); setMessages([]); }
    loadSessions();
  };

  return (
    <div className="relative h-screen bg-cg-bg flex overflow-hidden">
      <Starfield count={40} />

      {/* Sidebar */}
      <aside
        className={`fixed md:static z-40 h-full w-72 shrink-0 cg-glass border-r border-[rgba(140,79,214,0.22)] flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5 px-5 h-16 shrink-0 border-b border-[rgba(140,79,214,0.18)]">
          <DiamondMark size={24} />
          <span className="font-serif text-lg">ClearGlass<span className="text-cg-cyan">.ai</span></span>
        </button>

        <div className="p-4 space-y-2">
          <Button
            data-testid="new-session-button"
            onClick={() => setShowModePicker(true)}
            className="w-full rounded-xl bg-cg-violet hover:bg-cg-violetglow text-white justify-start gap-2"
          >
            <Plus className="h-4 w-4" /> New session
          </Button>
          <Button
            data-testid="open-journal-button"
            onClick={openJournal}
            variant="outline"
            className="w-full rounded-xl bg-transparent border-[rgba(140,79,214,0.3)] text-cg-texts hover:text-cg-textp hover:bg-[rgba(140,79,214,0.1)] justify-start gap-2"
          >
            <BookMarked className="h-4 w-4" /> Journal
          </Button>
        </div>

        <div className="px-4 pb-2 text-[10px] font-mono uppercase tracking-[0.2em] text-cg-textm">Your sessions</div>
        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1.5" data-testid="sessions-list">
          {sessions.length === 0 && (
            <p className="px-2 text-sm text-cg-textm">No sessions yet. Begin one above.</p>
          )}
          {sessions.map((s) => {
            const M = MODE_MAP[s.mode] || MODES[0];
            return (
              <button
                key={s.session_id}
                onClick={() => openSession(s)}
                data-testid={`session-item-${s.session_id}`}
                className={`group w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition-colors ${
                  active?.session_id === s.session_id ? "bg-[rgba(140,79,214,0.22)]" : "hover:bg-[rgba(140,79,214,0.1)]"
                }`}
              >
                <M.icon className="h-4 w-4 text-cg-cyan shrink-0" />
                <span className="flex-1 truncate text-sm text-cg-texts">{M.label}</span>
                <Trash2
                  onClick={(e) => deleteSession(e, s.session_id)}
                  className="h-3.5 w-3.5 text-cg-textm opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                />
              </button>
            );
          })}
        </div>

        <button onClick={() => navigate("/account")} className="shrink-0 border-t border-[rgba(140,79,214,0.18)] px-5 py-4 flex items-center gap-3 hover:bg-[rgba(140,79,214,0.08)] transition-colors" data-testid="account-link">
          <div className="h-8 w-8 rounded-full bg-cg-violet/40 grid place-items-center text-xs font-medium text-cg-textp">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="text-left">
            <p className="text-sm text-cg-textp truncate max-w-[150px]">{user?.name}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-cg-cyan">{user?.plan || "free"}</p>
          </div>
        </button>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <main className="relative z-10 flex-1 flex flex-col min-w-0">
        <div className="h-16 shrink-0 flex items-center gap-3 px-5 border-b border-[rgba(140,79,214,0.18)] cg-glass">
          <button data-testid="mobile-sidebar-toggle" className="md:hidden text-cg-texts" onClick={() => setSidebarOpen(true)}><Menu className="h-5 w-5" /></button>
          {active ? (
            <>
              <span className="font-serif text-lg text-cg-textp">{(MODE_MAP[active.mode] || MODES[0]).label}</span>
              <span className="hidden sm:inline whitespace-nowrap text-[10px] font-mono uppercase tracking-widest text-cg-violetglow px-2 py-0.5 rounded-full border border-[rgba(140,79,214,0.3)]">
                {(MODE_MAP[active.mode] || MODES[0]).tag}
              </span>
            </>
          ) : (
            <span className="font-serif text-lg text-cg-texts">Choose a portal to begin</span>
          )}
          {user?.use_puter && (
            <span
              data-testid="puter-ai-badge"
              title="Admin/demo sessions use Puter's free AI (your own Puter account), not the app's credits"
              className="inline-flex items-center gap-1.5 rounded-full border border-cg-gold/50 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest text-cg-gold bg-[rgba(242,181,68,0.08)]"
            >
              Demo AI · Puter
            </span>
          )}
          <button
            data-testid="sound-toggle-button"
            onClick={() => toggleSound()}
            title={soundOn ? "Mute ambient" : "Play cosmic ambient"}
            className={`ml-auto flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${
              soundOn
                ? "border-cg-cyan/50 text-cg-cyan bg-[rgba(92,200,240,0.08)]"
                : "border-[rgba(140,79,214,0.3)] text-cg-textm hover:text-cg-texts"
            }`}
          >
            {soundOn ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{soundOn ? "Ambient on" : "Ambient"}</span>
          </button>
        </div>

        {active?.mode === "manifest" && (
          <ManifestTracker step={parseStep(messages)} complete={messages.some((m) => m.role === "assistant" && m.content?.includes("[[/REWRITE]]"))} />
        )}

        {/* Log */}
        <div ref={logRef} className="flex-1 overflow-y-auto px-5 py-8" data-testid="chat-log">
          <div className="max-w-2xl mx-auto space-y-5">
            {!active && (
              <div className="text-center pt-20">
                <div className="animate-floaty w-fit mx-auto mb-6"><DiamondMark size={56} /></div>
                <h2 className="font-serif text-2xl font-light text-cg-textp mb-3">Pick up where you left off</h2>
                <p className="text-cg-texts mb-8 max-w-sm mx-auto">Open a past session on the left, or begin a new one through one of the five portals.</p>
                <Button data-testid="empty-new-session" onClick={() => setShowModePicker(true)} className="rounded-full bg-cg-violet hover:bg-cg-violetglow text-white px-7">
                  <Plus className="h-4 w-4 mr-2" /> New session
                </Button>
              </div>
            )}

            {active && messages.length === 0 && !sending && (
              <div className="text-center pt-16 text-cg-texts">
                <p className="font-serif text-xl text-cg-textp mb-2">{(MODE_MAP[active.mode] || MODES[0]).label}</p>
                <p className="text-sm max-w-sm mx-auto">{active.mode === "manifest" ? "Begin with what you desire most right now. I'll guide you step by step." : "Say the pattern or feeling in one plain sentence to begin."}</p>
              </div>
            )}

            {messages.map((m, idx) => (
              <motion.div
                key={m.message_id + idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "user" ? (
                  <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#E4DAF7] text-[#1D1540] px-4 py-3 text-sm shadow-md" data-testid="user-message">
                    {m.content}
                  </div>
                ) : (
                  <div className="max-w-[88%] group" data-testid="ai-message">
                    <div className="rounded-2xl rounded-tl-sm bg-cg-card border border-[rgba(140,79,214,0.3)] px-5 py-4 text-sm text-cg-textp leading-relaxed shadow-lg">
                      <AiContent content={m.content} onSave={saveToJournal} />
                    </div>
                    <button
                      onClick={() => { const p = splitAiContent(m.content); saveToJournal([p.before, p.rewrite, p.after].filter(Boolean).join("\n\n")); }}
                      data-testid="save-insight-button"
                      className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-cg-textm hover:text-cg-cyan transition-colors opacity-70 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Bookmark className="h-3 w-3" /> Save to journal
                    </button>
                  </div>
                )}
              </motion.div>
            ))}

            {sending && !messages.some((m) => m.message_id === "streaming") && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-tl-sm bg-cg-card border border-[rgba(140,79,214,0.3)] px-5 py-4 flex gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="h-2 w-2 rounded-full bg-cg-cyan/70 animate-twinkle" style={{ animationDelay: `${i * 0.2}s`, animationDuration: "1s" }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Compose */}
        {active && (
          <div className="shrink-0 border-t border-[rgba(140,79,214,0.18)] p-4 cg-glass">
            <div className="max-w-2xl mx-auto flex items-end gap-3">
              <Textarea
                data-testid="message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Say it in one plain sentence…"
                rows={1}
                className="resize-none bg-cg-overlay/60 border-[rgba(140,79,214,0.3)] text-cg-textp placeholder:text-cg-textm focus-visible:ring-cg-cyan rounded-2xl min-h-[48px] max-h-40"
              />
              <Button
                data-testid="send-message-button"
                onClick={send}
                disabled={sending || !input.trim()}
                className="h-12 w-12 shrink-0 rounded-2xl bg-cg-violet hover:bg-cg-violetglow text-white p-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Mode picker */}
      <Dialog open={showModePicker} onOpenChange={setShowModePicker}>
        <DialogContent className="bg-cg-card border-[rgba(140,79,214,0.3)] text-cg-textp max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Five portals to presence</DialogTitle>
            <DialogDescription className="text-cg-textm">Choose how you'd like to begin this session.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => startSession(m.id)}
                data-testid={`mode-option-${m.id}`}
                className="group text-left rounded-2xl p-5 bg-cg-overlay/40 border border-[rgba(140,79,214,0.25)] hover:border-cg-cyan/50 hover:-translate-y-0.5 transition-all"
              >
                <div className="h-10 w-10 rounded-xl grid place-items-center bg-[rgba(140,79,214,0.2)] text-cg-cyan mb-3">
                  <m.icon className="h-5 w-5" />
                </div>
                <p className="font-serif text-lg">{m.label}</p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-cg-violetglow mt-0.5">{m.tag}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Journal */}
      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent className="bg-cg-card border-[rgba(140,79,214,0.3)] text-cg-textp max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">Your journal</DialogTitle>
            <DialogDescription className="text-cg-textm">Insights you've saved from your reflections.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2" data-testid="journal-list">
            {journal.length === 0 && <p className="text-sm text-cg-textm">No saved insights yet. Tap "Save to journal" under any reply.</p>}
            {journal.map((e) => (
              <div key={e.entry_id} className="group rounded-2xl p-4 bg-cg-overlay/40 border border-[rgba(140,79,214,0.22)]">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-cg-violetglow">{(MODE_MAP[e.mode] || MODES[0]).label}</p>
                  <Trash2 onClick={() => deleteJournal(e.entry_id)} className="h-3.5 w-3.5 text-cg-textm hover:text-red-400 cursor-pointer shrink-0" data-testid={`delete-journal-${e.entry_id}`} />
                </div>
                <p className="text-sm text-cg-texts mt-2 whitespace-pre-wrap leading-relaxed">{e.content}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>{breath && <BreathOverlay onClose={() => setBreath(false)} />}</AnimatePresence>
    </div>
  );
}
